import type { ReactNode } from "react";

export interface SectionCardProps {
  /** Anchor id — enables `#hash` deep-linking / scroll. */
  id?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  /** Right-slot: segmented controls, action buttons, New-Soon badge, etc. */
  right?: ReactNode;
  /** When true, renders a small amber dot next to the title. */
  isDirty?: boolean;
  /** When true, dims the whole card (used for "(New Soon)" placeholder sections). */
  disabled?: boolean;
  /**
   * Visual variant. "default" keeps the bordered-header card used across the
   * app. "flat" is the Trioty look — a 20px-radius card with a borderless
   * header, a neutral (non-accent) icon tile, and padding-integrated spacing.
   */
  variant?: "default" | "flat";
  children: ReactNode;
  className?: string;
}

/**
 * The primary card surface used across the redesigned pages. Renders a
 * rounded white surface with a bordered header row (icon tile + title +
 * subtitle + right-slot) and a body slot.
 *
 * Always-expanded (no collapse affordance). Use the `id` prop for
 * deep-link scrolling. For sections gated behind "(New Soon)", pass the
 * `NewSoonBadge` into the `right` slot and set `disabled` to dim the body.
 */
export function SectionCard({
  id,
  title,
  subtitle,
  icon,
  right,
  isDirty = false,
  disabled = false,
  variant = "default",
  children,
  className = "",
}: SectionCardProps) {
  const flat = variant === "flat";
  return (
    <section
      id={id}
      className={[
        flat ? "rounded-[20px]" : "rounded-2xl",
        "border border-[color:var(--border-light)] bg-[color:var(--surface)]",
        "shadow-[0_1px_2px_rgba(15,20,40,0.04)]",
        disabled ? "opacity-70" : "",
        className,
      ].join(" ")}
    >
      <header
        className={
          flat
            ? "flex items-start gap-[11px] px-[18px] pb-[15px] pt-[18px]"
            : "flex items-center gap-2.5 border-b border-[color:var(--border-light)] px-[18px] py-3"
        }
      >
        {icon ? (
          <span
            aria-hidden="true"
            className={
              flat
                ? "inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[9px] border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] text-[color:var(--text-primary)]"
                : "inline-flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-md bg-[color-mix(in_oklab,var(--accent-500)_14%,white)] text-[color:var(--accent-600)]"
            }
          >
            {icon}
          </span>
        ) : null}
        <div className="flex min-w-0 flex-col">
          <span
            className={[
              "flex items-center gap-2 leading-tight tracking-tight text-[color:var(--text-primary)]",
              flat ? "text-[14.5px] font-bold" : "text-card-title font-semibold",
            ].join(" ")}
          >
            <span className="truncate">{title}</span>
            {isDirty ? (
              <span
                aria-hidden="true"
                className="h-2 w-2 shrink-0 rounded-full bg-[color:var(--accent-500)]"
              />
            ) : null}
          </span>
          {subtitle ? (
            <span
              className={[
                "truncate text-[color:var(--text-muted)]",
                flat ? "mt-px text-[12px]" : "text-meta",
              ].join(" ")}
            >
              {subtitle}
            </span>
          ) : null}
        </div>
        {right ? (
          <div className="ms-auto flex shrink-0 items-center gap-2">{right}</div>
        ) : null}
      </header>
      <div
        className={[
          flat ? "px-[18px] pb-5 pt-0" : "p-[18px]",
          disabled ? "pointer-events-none" : "",
        ].join(" ")}
      >
        {children}
      </div>
    </section>
  );
}
